import type * as React from 'react';

import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../lib/utils';
import { Separator } from './separator';

function ItemGroup({ className, ...props }: React.ComponentProps<'div'>) {
    return (
        <div
            role="list"
            data-slot="item-group"
            className={cn('group/item-group flex w-full flex-col', className)}
            {...props}
        />
    );
}

function ItemSeparator({ className, ...props }: React.ComponentProps<typeof Separator>) {
    return (
        <Separator
            data-slot="item-separator"
            orientation="horizontal"
            className={cn(className)}
            {...props}
        />
    );
}

const itemVariants = cva(
    'group/item flex w-full flex-wrap rounded-md text-sm transition-colors duration-100 outline-none overflow-clip focus-visible:border-ring focus-visible:ring-accent focus-visible:ring-[3px]',
    {
        variants: {
            variant: {
                default: 'border-transparent',
                outline: 'border border-border',
                muted: 'border-transparent bg-muted/50',
            },
            size: {
                default: 'gap-4 p-4',
                sm: 'gap-2.5 px-4 py-3',
                xs: 'gap-2 px-2.5 py-2 in-data-[slot=dropdown-menu-content]:p-0',
            },
            alignment: {
                top: 'items-start',
                center: 'items-center',
            },
            // For a row that is a button or link: asChild over the element, then this.
            interactive: {
                true: 'cursor-pointer text-left hover:bg-muted/50',
                false: '',
            },
        },
        defaultVariants: {
            variant: 'default',
            size: 'default',
            alignment: 'top',
            interactive: false,
        },
    },
);

function Item({
    className,
    variant = 'default',
    size = 'default',
    alignment = 'top',
    interactive = false,
    asChild = false,
    ...props
}: React.ComponentProps<'div'> & VariantProps<typeof itemVariants> & { asChild?: boolean }) {
    const Comp = asChild ? Slot : 'div';
    return (
        <Comp
            data-slot="item"
            data-variant={variant}
            data-size={size}
            data-alignment={alignment}
            className={cn(itemVariants({ variant, size, alignment, interactive, className }))}
            {...props}
        />
    );
}

const itemMediaVariants = cva(
    'flex shrink-0 items-center justify-center [&_svg]:pointer-events-none group-has-data-[slot=item-description]/item:self-start',
    {
        variants: {
            variant: {
                default: '[&_svg:not([class*="size-"])]:size-5',
                icon: 'size-8 rounded-sm border border-border bg-muted [&_svg:not([class*="size-"])]:size-4',
                image: 'size-10 overflow-hidden rounded-sm group-data-[size=sm]/item:size-8 group-data-[size=xs]/item:size-6 [&_img]:size-full [&_img]:object-cover',
                avatar: 'size-8 overflow-hidden rounded-full [&_img]:size-full [&_img]:object-cover',
                tile: 'size-10 flex-col rounded-sm bg-muted',
            },
        },
        defaultVariants: {
            variant: 'default',
        },
    },
);

function ItemMedia({
    className,
    variant = 'default',
    ...props
}: React.ComponentProps<'div'> & VariantProps<typeof itemMediaVariants>) {
    return (
        <div
            data-slot="item-media"
            data-variant={variant}
            className={cn(itemMediaVariants({ variant, className }))}
            {...props}
        />
    );
}

function ItemContent({ className, ...props }: React.ComponentProps<'div'>) {
    return (
        <div
            data-slot="item-content"
            className={cn(
                'flex flex-1 flex-col gap-1 group-data-[size=xs]/item:gap-0 [&+[data-slot=item-content]]:flex-none',
                className,
            )}
            {...props}
        />
    );
}

function ItemTitle({ className, ...props }: React.ComponentProps<'div'>) {
    return (
        <div
            data-slot="item-title"
            className={cn(
                'line-clamp-1 flex w-fit items-center gap-2 text-sm leading-4 font-semibold underline-offset-4',
                className,
            )}
            {...props}
        />
    );
}

function ItemDescription({ className, ...props }: React.ComponentProps<'p'>) {
    return (
        <p
            data-slot="item-description"
            className={cn(
                'line-clamp-2 text-left text-sm leading-5 font-normal text-muted-foreground group-data-[size=xs]/item:text-xs [&>a]:underline [&>a]:underline-offset-4 [&>a:hover]:text-primary',
                className,
            )}
            {...props}
        />
    );
}

function ItemActions({ className, ...props }: React.ComponentProps<'div'>) {
    return (
        <div
            data-slot="item-actions"
            className={cn('flex items-center gap-2', className)}
            {...props}
        />
    );
}

function ItemHeader({ className, ...props }: React.ComponentProps<'div'>) {
    return (
        <div
            data-slot="item-header"
            className={cn('flex basis-full items-center justify-between gap-2', className)}
            {...props}
        />
    );
}

function ItemFooter({ className, ...props }: React.ComponentProps<'div'>) {
    return (
        <div
            data-slot="item-footer"
            className={cn('flex basis-full items-center justify-between gap-2', className)}
            {...props}
        />
    );
}

export {
    Item,
    ItemActions,
    ItemContent,
    ItemDescription,
    ItemFooter,
    ItemGroup,
    ItemHeader,
    ItemMedia,
    ItemSeparator,
    ItemTitle,
};
